import { zodResolver } from "@hookform/resolvers/zod";
import {
  Modal,
  SelectField,
  TextAreaField,
  TextInputField,
  ToggleField,
  useToasts,
} from "@twinkltech/curriculum-architect-component-library";
import {
  ApiResponse,
  Curriculum,
  UpdateCurriculumPayload,
} from "@twinkltech/curriculum-architect-domain-hooks";
import { Button, Text } from "@twinkltech/web-ui";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { SubmitHandler, useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import z from "zod";

import { CurriculumAccessTogglePrompt } from "@/components/CurriculumAccessTogglePrompt/CurriculumAccessTogglePrompt";
import { GENERIC_CURRICULUM_FORM_NS } from "@/components/GenericCurriculumModal/GenericCurriculumModal.i18n";

import { usePreserveElementHeight } from "@/hooks/usePreserveElementHeight/usePreserveElementHeight";

import { COMMON_NS } from "@/i18n/common/common.i18n";
import { useFeedback } from "@/providers/Feedback/Feedback.context";
import { captureAppError } from "@/utils/monitoring/sentry";

import { REQUIRED_FIELD_MESSAGE } from "@/utils/validation-messages";

import { NEW_CURRICULUM_MODAL_NS } from "../../../NewCurriculumModal/NewCurriculumModal.i18n";
import { EDIT_CURRICULUM_MODAL_NS } from "../../EditCurriculumModal.i18n";

const CurriculumFormSchema = z.object({
  displayLabel: z.string().min(1, REQUIRED_FIELD_MESSAGE).max(120),
  description: z.string().max(500).optional(),
  status: z.enum(["active", "archived"]),
  progress: z.enum(["not_yet_planned", "planned", "planning_in_progress"]),
  access: z.boolean(),
});

type CurriculumFormData = z.infer<typeof CurriculumFormSchema>;

export const SchoolEditCurriculumModal = ({
  curriculum,
  error,
  isLoading,
  onClickClose,
  onPostSave,
  onSubmit,
  open,
  setOpen,
}: {
  curriculum?: Curriculum | null;
  error: Error | null;
  isLoading: boolean;
  open: boolean;
  onClickClose: () => void;
  onPostSave: () => void;
  onSubmit: (data: Partial<Curriculum>) => ApiResponse<Partial<Curriculum>>;
  setOpen: (open: boolean) => void;
}) => {
  const { t } = useTranslation(NEW_CURRICULUM_MODAL_NS);
  const { incrementEngagementCount } = useFeedback();

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { addErrorToast, addSuccessToast } = useToasts();

  const modalBodyRef = useRef<HTMLDivElement>(null);
  const [isAccessInfoVisible, setIsAccessInfoVisible] = useState(false);
  const { preserveHeight, styles: modalBodyStyles } =
    usePreserveElementHeight(modalBodyRef);

  const toggleAccessInfoVisibility = (show: boolean) => {
    preserveHeight(show);
    setIsAccessInfoVisible(show);
  };

  // Accessibility IDs
  const formId = useId();
  const currNameInputId = useId();
  const currDescriptionInputId = useId();
  const currVisibilityInputId = useId();
  const currPlanningProgressInputId = useId();

  const formDefaultValues: CurriculumFormData = useMemo(
    () => ({
      displayLabel: curriculum?.displayLabel || "",
      description: curriculum?.description || "",
      status: curriculum?.status || "active",
      progress: curriculum?.progress || "not_yet_planned",
      access: curriculum?.ownerType === "SCHOOL",
    }),
    [curriculum]
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    reset,
  } = useForm<CurriculumFormData, unknown, CurriculumFormData>({
    resolver: zodResolver<CurriculumFormData, unknown, CurriculumFormData>(
      CurriculumFormSchema
    ),
    defaultValues: formDefaultValues,
  });

  // Note: Need to use a controller for the Headless UI custom components
  const { field: accessField } = useController({
    control,
    name: "access",
    defaultValue: formDefaultValues.access,
    disabled: curriculum?.ownerType === "SCHOOL",
  });
  const { field: progressField } = useController({
    control,
    name: "progress",
    defaultValue: formDefaultValues.progress,
  });
  const { field: statusField } = useController({
    control,
    name: "status",
    defaultValue: formDefaultValues.status,
  });

  const onHandleOnClose = () => {
    setOpen(false);
  };

  const onFormSubmit: SubmitHandler<CurriculumFormData> = async (
    data,
    event
  ) => {
    event?.preventDefault();

    if (isLoading || isSubmitting) {
      return;
    }

    incrementEngagementCount();

    // only map the ones that have changed

    const mappedData: UpdateCurriculumPayload = {
      displayLabel: data.displayLabel,
      description: data.description,
      status: data.status,
      progress: data.progress,
      // Making a curriculum public is irreversible.
      public: data.access || undefined,
    };

    setIsSubmitting(true);
    const result = await onSubmit(mappedData);

    if (result instanceof Error) {
      captureAppError(result, {
        action: "update",
        area: "curriculum",
        extra: {
          component: "SchoolEditCurriculumModal",
          curriculumId: curriculum?.id || null,
        },
      });
      addErrorToast({
        message: "Update failed",
      });
    } else {
      addSuccessToast({
        message: "Curriculum updated successfully",
      });
      onHandleOnClose();
    }

    onPostSave?.();

    setIsSubmitting(false);
  };

  // Set form values after loading and curriculum is available
  useEffect(() => {
    if (!isLoading && curriculum) {
      reset(formDefaultValues);
    }
  }, [isLoading, curriculum, reset, formDefaultValues]);

  if (error) {
    return (
      <Modal
        hasCloseButton
        open={open}
        width="sm"
        onClose={() => setOpen(false)}
      >
        <Modal.Body>Error: {error.message}</Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal
      hasCloseButton
      open={open}
      width="md"
      onClose={() => {
        onClickClose();
        onHandleOnClose();
      }}
    >
      <Modal.Header>
        <Modal.Title>Edit Curriculum</Modal.Title>
      </Modal.Header>
      <Modal.Body
        ref={modalBodyRef}
        className="mb-2 max-h-[75vh] overflow-y-auto"
        style={modalBodyStyles}
      >
        <form
          className="py-2"
          id={formId}
          onSubmit={handleSubmit(onFormSubmit)}
        >
          <div role="group">
            <Text className="mb-3" variant="xl-medium">
              {t("access-group.title")}
            </Text>
            <ToggleField
              checked={accessField.value}
              disabled={curriculum?.ownerType === "SCHOOL"}
              infoText={
                accessField.value
                  ? t("access-group.school-access-description")
                  : "Private curricula can only be viewed and edited by you"
              }
              label={t("access-group.label")}
              options={{
                on: "Yes (shared with School)",
                off: "No (Private)",
              }}
              {...register("access")}
              onChange={(value) => {
                accessField.onChange(value);
                toggleAccessInfoVisibility(value);
              }}
            />
            {isAccessInfoVisible && (
              <CurriculumAccessTogglePrompt
                onCancel={() => {
                  toggleAccessInfoVisibility(false);
                  //on cancel the value should be reset to false
                  accessField.onChange(false);
                }}
                onConfirm={() => {
                  toggleAccessInfoVisibility(false);
                }}
              />
            )}
          </div>

          <div className="my-6 4 border-b" />

          <div className="space-y-3" role="group">
            <Text className="mb-3" variant="xl-medium">
              {t("details-group.title")}
            </Text>
            <TextInputField
              disabled={isAccessInfoVisible}
              id={currNameInputId}
              infoText={t("inputs.name.info", {
                ns: GENERIC_CURRICULUM_FORM_NS,
              })}
              label={t("inputs.name.label", { ns: GENERIC_CURRICULUM_FORM_NS })}
              maxLength={120}
              placeholder={t("inputs.name.placeholder", {
                ns: GENERIC_CURRICULUM_FORM_NS,
              })}
              {...register("displayLabel", { required: true })}
              defaultValue={formDefaultValues.displayLabel}
              errorText={errors.displayLabel?.message}
            />
            <TextAreaField
              disabled={isAccessInfoVisible}
              id={currDescriptionInputId}
              infoText={t("inputs.description.info", {
                ns: GENERIC_CURRICULUM_FORM_NS,
              })}
              label={t("inputs.description.label", {
                ns: GENERIC_CURRICULUM_FORM_NS,
              })}
              maxLength={500}
              placeholder={t("inputs.description.placeholder", {
                ns: GENERIC_CURRICULUM_FORM_NS,
              })}
              {...register("description")}
              defaultValue={formDefaultValues.description}
              errorText={errors.description?.message}
            />
            <SelectField
              disabled={isAccessInfoVisible}
              id={currVisibilityInputId}
              label={t("inputs.status.label", {
                ns: GENERIC_CURRICULUM_FORM_NS,
              })}
              options={[
                { value: "active", label: "Active" },
                { value: "archived", label: "Archived" },
              ]}
              {...statusField}
              errorText={errors.status?.message}
              value={statusField.value}
            />
            <SelectField
              disabled={isAccessInfoVisible}
              id={currPlanningProgressInputId}
              label={t("inputs.progress.label", {
                ns: GENERIC_CURRICULUM_FORM_NS,
              })}
              options={[
                { value: "not_yet_planned", label: "Not yet planned" },
                {
                  value: "planning_in_progress",
                  label: "Planning in progress",
                },
                { value: "planned", label: "Planned" },
              ]}
              {...progressField}
              errorText={errors.progress?.message}
              value={progressField.value}
            />
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer className="gap-2">
        <Button
          disabled={isAccessInfoVisible || isSubmitting}
          type="button"
          variant="outlined"
          onClick={() => {
            onClickClose();
            onHandleOnClose();
          }}
        >
          {t("actions.cancel", { ns: COMMON_NS })}
        </Button>
        <Button
          disabled={isLoading || !open || isSubmitting || isAccessInfoVisible}
          form={formId}
          variant="primary"
        >
          {t("actions.save", { ns: EDIT_CURRICULUM_MODAL_NS })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
